<script lang="ts">
  import { onDestroy, createEventDispatcher } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { Check, Loader2, Trash2, Upload } from 'lucide-svelte';

  import {
    adoptList,
    customerList,
    deleteList,
    forgetList,
    listExcluded,
    applyExclusion,
    searchToRerun,
    uploadList,
    watchList
  } from '$lib/api/lists';
  import { submitFilters } from '$lib/api/filters';
  import { submitNeighborSamples } from '$lib/api/neighbors';
  import { applicationMode } from '$lib/stores/mode';
  import { neighborSamples } from '$lib/stores/neighbors';
  import { queryResult } from '$lib/stores/selection';
  import { notifications } from '$lib/stores/notifications';
  import { ApiError } from '$lib/api/client';

  /**
   * The customer's own list: uploading it, watching it match, subtracting it.
   *
   * # Why this is its own component
   *
   * It is asked for in two places — the button beside the results, and the last
   * step of the guide — and they are the same thing seen twice, not two
   * features. Two copies would drift: one would learn that a list still being
   * matched can already be subtracted, and the other would not.
   *
   * # Why the panel shows the code
   *
   * The list belongs to nobody in particular — there are no accounts — so the
   * code is the only way back to it. Somebody who uploads a list on Monday and
   * comes back on Tuesday, or who works in a second tab, needs it in front of
   * them rather than buried in local storage.
   *
   * # Why deleting is offered next to putting aside
   *
   * They are different things and both are wanted. Putting aside keeps the list
   * on the server and stops subtracting with it; deleting takes it off our
   * machines. Somebody who has just uploaded their entire customer base should
   * not have to ask us to do the second.
   */

  /** Roomier type and spacing, for the step that has a screen to itself. */
  export let roomy = false;

  const dispatch = createEventDispatcher<{ done: void }>();

  let busy = false;
  let copied = false;
  let enteringCode = false;
  let code = '';
  let fileInput: HTMLInputElement | undefined;
  let codeInput: HTMLInputElement | undefined;
  let stopWatching: (() => void) | undefined;

  $: list = $customerList;
  $: working = list?.status === 'pending' || list?.status === 'matching';

  // Follow a list that is still being matched, and stop when it is done.
  $: if (working) {
    startWatching();
  } else {
    stopWatching?.();
    stopWatching = undefined;
  }

  function startWatching() {
    if (stopWatching) return;
    stopWatching = watchList((updated) => {
      if (updated.status === 'ready' && $listExcluded) {
        // The counts on screen were computed before the subtraction had
        // finished, and are now larger than what the customer would be sold.
        refreshResults();
      }
    });
  }

  onDestroy(() => stopWatching?.());

  /**
   * Re-run the search, so the results reflect the subtraction.
   *
   * Which search that is depends on the mode, as it does when a shared link is
   * opened. A similarity selection is defined by its example companies, not by
   * the filters: running the classic search for it asks the app to search with
   * no criteria at all, which it refuses — and the customer, who had only
   * ticked a box, was told "an unexpected error occurred" and got no
   * subtraction.
   */
  async function refreshResults() {
    const which = searchToRerun(
      $applicationMode,
      $neighborSamples.length > 0,
      $queryResult.length > 0
    );

    if (which === 'neighbor') await submitNeighborSamples();
    else if (which === 'classic') await submitFilters();
  }

  async function onFile(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) await ingest(file);
    // Cleared so that choosing the same file again is a new upload rather than
    // a change event that never fires.
    input.value = '';
  }

  /**
   * Whether a file is being dragged over the drop area.
   *
   * Counted rather than set, because `dragleave` fires when the pointer
   * crosses onto a *child* of the drop area — the icon, the button, the label.
   * A boolean flickers off every time the cursor moves over one of them.
   */
  let dragDepth = 0;

  function onDrop(event: DragEvent) {
    event.preventDefault();
    dragDepth = 0;
    const file = event.dataTransfer?.files?.[0];
    if (file) void ingest(file);
  }

  /** The upload, from wherever the file came. */
  async function ingest(file: File) {
    busy = true;
    try {
      const uploaded = await uploadList(file);

      // Only once the matching is done.
      //
      // This used to re-run the search the moment the upload was accepted,
      // while the list was still being matched — so the results were rebuilt
      // against a subtraction of almost nothing, and then rebuilt again when
      // the watcher saw it finish. The customer watched their selection change
      // twice, the first time to a number that was never true.
      //
      // The exclusion is still applied to the filters immediately, so anything
      // asked for from here on already counts it. It is the re-running of a
      // search whose answer is about to change that this drops.
      if (uploaded.status === 'ready') await refreshResults();
    } catch (error) {
      const status = error instanceof ApiError ? error.status : 0;
      const reason =
        error instanceof ApiError &&
        (error.data as { error?: string } | undefined)?.error === 'no_columns'
          ? $_('customerList.failedColumns')
          : $_('customerList.failedUpload');

      notifications.error(status === 413 ? $_('customerList.failedUpload') : reason);
    } finally {
      busy = false;
    }
  }

  /**
   * Ask for a code, and put the cursor in the field.
   *
   * Without the focus the change is nearly invisible: the link that was clicked
   * is replaced by a box in the same place, and a placeholder repeating the
   * same words reads as text rather than as a field waiting for typing. It
   * looked like the button did nothing.
   */
  function askForCode() {
    enteringCode = true;
    // After the field exists — this runs before Svelte has rendered it.
    setTimeout(() => codeInput?.focus(), 0);
  }

  async function onAdopt() {
    busy = true;
    try {
      const adopted = await adoptList(code);
      if (!adopted) {
        notifications.error($_('customerList.unknownCode'));
        return;
      }
      code = '';
      enteringCode = false;
      await refreshResults();
    } finally {
      busy = false;
    }
  }

  async function onToggleExclude(event: Event) {
    listExcluded.set((event.target as HTMLInputElement).checked);
    applyExclusion();
    await refreshResults();
  }

  async function onDelete() {
    busy = true;
    try {
      await deleteList();
      notifications.success($_('customerList.deleted'));
      await refreshResults();
    } finally {
      busy = false;
      dispatch('done');
    }
  }

  async function onRemove() {
    forgetList();
    await refreshResults();
    dispatch('done');
  }

  async function copyCode() {
    if (!list) return;
    try {
      await navigator.clipboard.writeText(list.code);
      copied = true;
      setTimeout(() => (copied = false), 2500);
    } catch {
      notifications.info(list.code);
    }
  }
</script>

{#if !list}
  {#if roomy}
    <!--
      One object, not five stacked ones.

      The compact panel's shape was being reused at a size it was never drawn
      for: an intro paragraph, a column-list paragraph, a button, a formats
      line and a code link, left-aligned down a 2xl column in four type sizes
      and three greys. Five separate things to read before the one thing to do.

      The two paragraphs are gone from this variant rather than restyled,
      because the door already says both — better, and without contradicting
      itself. `guided.listUpload.subtitle` is above this panel in the screen
      header and explains what the file is for; `guided.listUpload.hint` is
      below it and names the columns that help. The panel's own `intro` says
      the file will be *excluded so you do not buy twice*, which is true in
      the data view and the opposite of what this door does with it: here the
      file is the input the search is built from. Two framings of one upload,
      one paragraph apart.

      So what is left is the action, as one bordered area you can also drop
      onto.
    -->
    <div
      role="presentation"
      on:dragenter|preventDefault={() => (dragDepth += 1)}
      on:dragover|preventDefault
      on:dragleave={() => (dragDepth = Math.max(0, dragDepth - 1))}
      on:drop={onDrop}
      class="flex flex-col items-center justify-center gap-3 rounded-surface border-2 border-dashed px-6 py-10 text-center transition-colors {dragDepth >
      0
        ? 'border-accent-400 bg-accent-50'
        : 'border-hairline-strong'}"
    >
      <span class="flex h-12 w-12 items-center justify-center rounded-full bg-accent-50">
        <Upload class="h-6 w-6 text-accent-600" />
      </span>

      <p class="text-sm text-gray-600">{$_('customerList.dropHere')}</p>

      <button
        type="button"
        class="flex items-center justify-center gap-2 rounded-control bg-accent-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-accent-700 disabled:opacity-60"
        disabled={busy}
        on:click={() => fileInput?.click()}
      >
        {#if busy}
          <Loader2 class="h-4 w-4 animate-spin" />
          {$_('customerList.uploading')}
        {:else}
          {$_('customerList.choose')}
        {/if}
      </button>

      <p class="text-xs text-gray-500">{$_('customerList.formats')}</p>
    </div>
  {:else}
    <p class="text-xs leading-relaxed text-gray-600">
      {$_('customerList.intro')}
    </p>
    <p class="mt-2 text-xs leading-relaxed text-gray-400">
      {$_('customerList.columns')}
    </p>

    <button
      type="button"
      class="mt-3 flex w-full items-center justify-center gap-2 rounded-control bg-accent-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-700 disabled:opacity-60"
      disabled={busy}
      on:click={() => fileInput?.click()}
    >
      {#if busy}
        <Loader2 class="h-4 w-4 animate-spin" />
        {$_('customerList.uploading')}
      {:else}
        <Upload class="h-4 w-4" />
        {$_('customerList.choose')}
      {/if}
    </button>
    <p class="mt-1 text-center text-[11px] text-gray-400">
      {$_('customerList.formats')}
    </p>
  {/if}

  <input
    bind:this={fileInput}
    type="file"
    accept=".csv,.txt,.tsv,.xlsx,.xlsm"
    class="hidden"
    on:change={onFile}
  />

  <div class="mt-4 border-t border-hairline pt-3">
    {#if enteringCode}
      <label class="mb-1 block text-[11px] uppercase tracking-wide text-gray-400" for="list-code">
        {$_('customerList.code')}
      </label>
      <div class="flex max-w-sm gap-2">
        <input
          id="list-code"
          type="text"
          bind:this={codeInput}
          bind:value={code}
          placeholder={$_('customerList.codePlaceholder')}
          class="min-w-0 flex-1 rounded-control border border-hairline px-2 py-1.5 text-sm focus:border-accent-400 focus:outline-none"
          on:keydown={(e) => e.key === 'Enter' && onAdopt()}
        />
        <button
          type="button"
          class="rounded-control border border-hairline px-2.5 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-hairline-strong disabled:opacity-60"
          disabled={busy || !code.trim()}
          on:click={onAdopt}
        >
          {$_('customerList.adopt')}
        </button>
      </div>
    {:else}
      <button
        type="button"
        class="text-xs text-gray-500 underline-offset-2 transition-colors hover:text-gray-800 hover:underline"
        on:click={askForCode}
      >
        {$_('customerList.enterCode')}
      </button>
    {/if}
  </div>
{:else if working}
  <div class="flex items-center gap-2 text-sm text-gray-700">
    <Loader2 class="h-4 w-4 animate-spin text-accent-600" />
    {$_('customerList.matching')}
  </div>
  <p class="mt-2 text-xs leading-relaxed text-gray-500">{$_('customerList.matchingHint')}</p>
  <p class="mt-3 text-xs text-gray-400">{list.name}</p>
{:else if list.status === 'failed'}
  <p class="text-sm text-red-600">{$_('customerList.failedMatch')}</p>
  {#if list.failure}
    <p class="mt-1 break-words text-xs text-gray-500">{list.failure}</p>
  {/if}
  <button
    type="button"
    class="mt-3 rounded-control border border-hairline px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-hairline-strong {roomy
      ? ''
      : 'w-full'}"
    on:click={onRemove}
  >
    {$_('customerList.remove')}
  </button>
{:else}
  <p class="font-medium text-gray-900 {roomy ? 'text-base' : 'text-sm'}">
    {$_('customerList.ready', { values: { matched: list.matched, rows: list.rows } })}
  </p>
  <p class="mt-0.5 truncate text-xs text-gray-400">{list.name}</p>

  <label class="mt-3 flex cursor-pointer items-center gap-2 text-sm text-gray-700">
    <input
      type="checkbox"
      checked={$listExcluded}
      on:change={onToggleExclude}
      class="h-4 w-4 rounded border-hairline text-accent-600 focus:ring-accent-500"
    />
    {$_('customerList.exclude')}
  </label>

  <div class="mt-4 max-w-sm border-t border-hairline pt-3">
    <div class="flex items-center justify-between gap-2">
      <div class="min-w-0">
        <div class="text-[11px] uppercase tracking-wide text-gray-400">
          {$_('customerList.code')}
        </div>
        <div class="truncate font-mono text-sm text-gray-800">{list.code}</div>
      </div>
      <button
        type="button"
        class="shrink-0 rounded-control border border-hairline px-2 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-hairline-strong hover:text-gray-900"
        on:click={copyCode}
      >
        {#if copied}
          <Check class="h-3.5 w-3.5 text-accent-600" />
        {:else}
          {$_('customerList.copy')}
        {/if}
      </button>
    </div>
    <p class="mt-1 text-[11px] leading-relaxed text-gray-400">{$_('customerList.codeHint')}</p>
  </div>

  <div class="mt-3 flex max-w-sm gap-2">
    <button
      type="button"
      class="flex-1 rounded-control border border-hairline px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:border-hairline-strong"
      on:click={onRemove}
    >
      {$_('customerList.remove')}
    </button>
    <button
      type="button"
      class="flex flex-1 items-center justify-center gap-1.5 rounded-control border border-red-200 px-3 py-2 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60"
      disabled={busy}
      on:click={onDelete}
    >
      <Trash2 class="h-3.5 w-3.5" />
      {$_('customerList.delete')}
    </button>
  </div>

  <p class="mt-2 text-[11px] leading-relaxed text-gray-400">{$_('customerList.retention')}</p>
{/if}
